import { Button, useModal } from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { useRegenerateRdProjectDetailsMutation } from '@neo.tax/rd/hooks';
import { BackgroundOperationName } from '@neo.tax/shared/graphql';
import { useBackgroundOperations, useUserFromAuth } from '@neo.tax/shared/hooks';

import { RegenerateRdProjectDetailsModal, RegenerateRdProjectDetailsOptions } from './RegenerateRdProjectDetailsModal';

/**
 * Super-user entry point for regenerating AI-generated details across a fiscal year's R&D projects.
 * Shown only while a super user is viewing a company as one of its users, so it never appears in a
 * view a customer could have.
 */
export const RegenerateRdProjectDetailsAction = (): ReactElement | null => {
  const { isSuperUserMode } = useUserFromAuth();
  const { fiscalYear, fiscalYearId } = useRdContext();
  const { backgroundOperations, loading: loadingBackgroundOperations } = useBackgroundOperations();
  const { isOpen, setIsOpen } = useModal();
  const [regenerateRdProjectDetails, { loading }] = useRegenerateRdProjectDetailsMutation();

  if (!isSuperUserMode) {
    return null;
  }

  const isRunInProgress =
    backgroundOperations?.some(
      (operation) =>
        operation.name === BackgroundOperationName.RegenerateRdProjectDetailsOperation &&
        operation.progressPercent < 100,
    ) ?? false;

  const handleSubmit = async (options: RegenerateRdProjectDetailsOptions, projectCount: number): Promise<void> => {
    try {
      await regenerateRdProjectDetails({
        variables: { fiscalYearId, input: options },
        context: {
          loadingToast: {
            title: 'Project details update started',
            description: `${projectCount} projects are queued. The projects page is locked until the job finishes.`,
          },
          errorToast: {
            title: 'Failed to start the project details update',
            description: 'An unexpected error happened. Please try again.',
          },
        },
      });
    } finally {
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        size="lg"
        variant="white"
        rounded="regular"
        disabled={loadingBackgroundOperations || isRunInProgress}
        onClick={(): void => setIsOpen(true)}
      >
        Update Details
      </Button>
      <RegenerateRdProjectDetailsModal
        fiscalYearId={fiscalYearId}
        fiscalYearLabel={String(fiscalYear.year)}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        loading={loading}
        onSubmit={handleSubmit}
      />
    </>
  );
};
